import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronRight } from "lucide-react";

const filters = [
  { key: "all", label: "Tutti" },
  { key: "1", label: "Blocco 1" },
  { key: "2", label: "Blocco 2" },
  { key: "3", label: "Blocco 3" },
  { key: "4", label: "Blocco 4" },
];

export default function Moduli() {
  const [modules, setModules] = useState(null);
  const [filter, setFilter] = useState("all");
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get("/modules").then((r) => setModules(r.data)).catch(() => setError(true));
  }, []);

  if (error) {
    return <p className="pt-10 text-center font-bold">Impossibile caricare i moduli. Ricarica la pagina.</p>;
  }

  const list = modules ? (filter === "all" ? modules : modules.filter((m) => String(m.blocco) === filter)) : [];

  return (
    <div className="pt-6">
      <h1 className="text-3xl font-extrabold tracking-tight fade-up">I moduli.</h1>
      <p className="mt-2 text-sm text-[color:var(--text-2)] leading-6 fade-up" data-testid="modules-block-note">
        4 moduli · 4 blocchi d'esame. Uno per blocco. Idoneo con ≥9/15 in ciascuno.
      </p>

      {/* Filter */}
      <div className="mt-5 flex gap-2 overflow-x-auto pb-1 -mx-4 px-4" data-testid="modules-filter-tabs">
        {filters.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`modules-filter-${f.key}`}
            aria-pressed={filter === f.key}
            className={`press shrink-0 rounded-full px-4 py-2 min-h-[36px] text-xs font-bold font-mono uppercase tracking-wide border transition-colors duration-200 ${
              filter === f.key
                ? "bg-[color:var(--ink)] text-white border-[color:var(--ink)]"
                : "bg-white text-[color:var(--ink)] border-[color:var(--hairline)] hover:bg-[color:var(--bg-subtle)]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="mt-5 space-y-3">
        {!modules &&
          [1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24 w-full rounded-3xl" />)}
        {modules && list.length === 0 && (
          <p className="text-sm text-[color:var(--text-2)] py-8 text-center">Nessun modulo per questo blocco.</p>
        )}
        {list.map((m, listIdx) => {
          const pct = m.totalLessons ? Math.round((m.completedLessons / m.totalLessons) * 100) : 0;
          return (
            <Link
              key={m.id}
              to={`/moduli/${m.id}`}
              data-testid={`module-card-${m.id}`}
              className="block rounded-3xl border border-[color:var(--hairline)] bg-white p-5 hover:bg-[color:var(--bg-subtle)] transition-colors duration-200 fade-up"
              style={{ animationDelay: `${Math.min(listIdx, 6) * 45}ms` }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[10px] tracking-widest uppercase border border-[color:var(--hairline)] rounded-full px-2 py-0.5">
                      Blocco {m.blocco}
                    </span>
                    <span className="font-mono text-[11px] text-[color:var(--text-2)]">{m.ore}h</span>
                  </div>
                  <h2 className="mt-2 font-extrabold tracking-tight leading-snug">{m.titolo}</h2>
                  <p className="mt-1 text-xs text-[color:var(--text-2)] leading-5 line-clamp-2">{m.descrizione}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-[color:var(--text-2)] shrink-0 mt-1" />
              </div>
              <div className="mt-4 flex items-center gap-3">
                <div
                  className="h-1 flex-1 rounded-full bg-[color:var(--bg-subtle)] overflow-hidden"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={pct}
                  aria-label={`Avanzamento modulo ${pct}%`}
                >
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${pct}%`, background: pct === 100 ? "var(--volt)" : "var(--ink)" }}
                  />
                </div>
                <span className="font-mono text-[11px] text-[color:var(--text-2)]">
                  {m.completedLessons}/{m.totalLessons}
                </span>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
